import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CopyResultButton } from "@/components/CopyResultButton";

export default function APRCalculator() {
  const [loanAmount, setLoanAmount] = useState("");
  const [interestRate, setInterestRate] = useState("");
  const [fees, setFees] = useState("");
  const [term, setTerm] = useState("");
  const [result, setResult] = useState<number | null>(null);

  const calculate = () => {
    const P = parseFloat(loanAmount);
    const nominalRate = parseFloat(interestRate) / 100;
    const F = parseFloat(fees);
    const years = parseFloat(term);

    if (isNaN(P) || isNaN(nominalRate) || isNaN(F) || isNaN(years) || P <= 0 || nominalRate < 0 || F < 0 || years <= 0) {
      toast.error("Please enter valid values");
      return;
    }

    const effectivePrincipal = P - F;
    const monthlyRate = nominalRate / 12;
    const months = years * 12;
    const monthlyPayment = P * (monthlyRate * Math.pow(1 + monthlyRate, months)) / (Math.pow(1 + monthlyRate, months) - 1);
    
    // Simplified APR approximation
    const totalPaid = monthlyPayment * months;
    const totalInterest = totalPaid - effectivePrincipal;
    const apr = (totalInterest / effectivePrincipal / years) * 100;

    setResult(apr);
    toast.success("APR calculated!");
  };

  const reset = () => {
    setLoanAmount("");
    setInterestRate("");
    setFees("");
    setTerm("");
    setResult(null);
  };

  return (
    <PageLayout>
      <Card>
        <CardHeader>
          <CardTitle>APR Calculator</CardTitle>
          <CardDescription>
            Calculate Annual Percentage Rate including fees
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="loanAmount">Loan Amount ($)</Label>
            <Input
              id="loanAmount"
              type="number"
              placeholder="10000"
              value={loanAmount}
              onChange={(e) => setLoanAmount(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="interestRate">Nominal Interest Rate (%)</Label>
            <Input
              id="interestRate"
              type="number"
              placeholder="7"
              value={interestRate}
              onChange={(e) => setInterestRate(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="fees">Total Fees ($)</Label>
            <Input
              id="fees"
              type="number"
              placeholder="500"
              value={fees}
              onChange={(e) => setFees(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="term">Loan Term (years)</Label>
            <Input
              id="term"
              type="number"
              placeholder="5"
              value={term}
              onChange={(e) => setTerm(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <Button onClick={calculate}>Calculate</Button>
            <Button onClick={reset} variant="outline">Reset</Button>
          </div>
          {result !== null && (
            <div className="mt-4 p-4 bg-muted rounded-lg">
              <div className="flex items-center justify-between">
                <p className="text-lg break-all overflow-wrap-anywhere">
                  <strong>APR:</strong> {result.toFixed(2)}%
                </p>
                <CopyResultButton value={result.toFixed(2)} />
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </PageLayout>
  );
}
